/**
 * Diosa Forever — one storefront tracking client.
 *
 * Everything the site knows is expressed once as a canonical Diosa event. This file
 * then (a) reports it to our own backend and (b) fans it out to whichever ad/analytics
 * destinations the Admin has enabled, using the mapping the backend hands down at
 * runtime. There is no per-platform event code anywhere else in the app.
 *
 * Money events are NOT trusted from here — the backend emits purchase / payment_success
 * from verified order state. The browser only ever reports intent.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AID_KEY = 'diosa_aid';
const SID_KEY = 'diosa_sid';
const ATTR_KEY = 'diosa_attr';
const CONSENT_KEY = 'diosa_consent';
const SESSION_MINUTES = 30;

let config = null;
let queue = [];
let flushTimer = null;
const loaded = new Set();

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};

export const anonymousId = () => {
  let id = ls.get(AID_KEY);
  if (!id) { id = uuid(); ls.set(AID_KEY, id); }
  return id;
};

export const sessionId = () => {
  const now = Date.now();
  let raw = ls.get(SID_KEY);
  let s = null;
  try { s = raw ? JSON.parse(raw) : null; } catch { s = null; }
  if (!s || now - s.at > SESSION_MINUTES * 60000) s = { id: uuid(), at: now };
  else s.at = now;
  ls.set(SID_KEY, JSON.stringify(s));
  return s.id;
};

const cookie = (name) => {
  const m = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return m ? decodeURIComponent(m[2]) : undefined;
};

/** First-touch is captured once per browser; last-touch refreshes on every tagged entry. */
const captureAttribution = () => {
  const q = new URLSearchParams(window.location.search);
  const touch = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'ttclid', 'msclkid'].forEach((k) => { if (q.get(k)) touch[k] = q.get(k); });
  const ref = document.referrer && !document.referrer.includes(window.location.host)
    ? document.referrer : '';
  let stored = {};
  try { stored = JSON.parse(ls.get(ATTR_KEY) || '{}'); } catch { stored = {}; }
  const hasSignal = Object.keys(touch).length > 0 || ref;
  if (hasSignal) {
    const next = { ...touch, referrer: ref || undefined, landing_page: window.location.pathname };
    stored = { first: stored.first || next, last: next };
    ls.set(ATTR_KEY, JSON.stringify(stored));
  }
  return { ...(stored.last || {}), ...touch };
};

export const consentState = () => ls.get(CONSENT_KEY) || 'unset';
export const setConsent = (value) => {
  ls.set(CONSENT_KEY, value);
  if (value === 'accepted') loadDestinations();
};

const adsAllowed = () => {
  const mode = config?.consent_mode || 'none';
  if (mode === 'none') return true;
  return consentState() === 'accepted';
};
const analyticsAllowed = () => {
  const mode = config?.consent_mode || 'none';
  if (mode === 'all') return consentState() === 'accepted';
  return true;
};
const allowed = (kind) => (kind === 'ads' ? adsAllowed() : analyticsAllowed());

const script = (src, attrs = {}) => new Promise((resolve) => {
  if (loaded.has(src)) return resolve();
  loaded.add(src);
  const el = document.createElement('script');
  el.async = true;
  el.src = src;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  el.onload = resolve;
  el.onerror = resolve;
  document.head.appendChild(el);
});

const dest = (slug) => (config?.destinations || []).find((d) => d.slug === slug);

/* ── destination loaders (public IDs only, read from Admin config at runtime) ── */

async function loadDestinations() {
  if (!config) return;
  for (const d of config.destinations) {
    if (!allowed(d.kind) || loaded.has(`init:${d.slug}`)) continue;
    const c = d.config || {};
    try {
      if (d.slug === 'gtm' && c.container_id) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
        await script(`https://www.googletagmanager.com/gtm.js?id=${c.container_id}`);
      }
      if ((d.slug === 'ga4' && c.measurement_id) || (d.slug === 'google-ads' && c.conversion_id)) {
        const id = d.slug === 'ga4' ? c.measurement_id : c.conversion_id;
        window.dataLayer = window.dataLayer || [];
        if (!window.gtag) window.gtag = function () { window.dataLayer.push(arguments); };
        await script(`https://www.googletagmanager.com/gtag/js?id=${id}`);
        window.gtag('js', new Date());
        window.gtag('config', id, { send_page_view: false });
      }
      if (d.slug === 'meta-pixel' && c.pixel_id) {
        if (!window.fbq) {
          const n = (window.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          });
          n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
        }
        await script('https://connect.facebook.net/en_US/fbevents.js');
        window.fbq('init', c.pixel_id);
      }
      if (d.slug === 'tiktok-pixel' && c.pixel_id) {
        window.TiktokAnalyticsObject = 'ttq';
        const ttq = (window.ttq = window.ttq || []);
        ttq.methods = ['page', 'track', 'identify', 'instances'];
        ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat([].slice.call(arguments, 0))); }; };
        ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
        await script(`https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${c.pixel_id}&lib=ttq`);
      }
      if (d.slug === 'pinterest-tag' && c.tag_id) {
        if (!window.pintrk) {
          window.pintrk = function () { window.pintrk.queue.push(Array.prototype.slice.call(arguments)); };
          window.pintrk.queue = []; window.pintrk.version = '3.0';
        }
        await script('https://s.pinimg.com/ct/core.js');
        window.pintrk('load', c.tag_id);
      }
      if (d.slug === 'snapchat-pixel' && c.pixel_id) {
        if (!window.snaptr) {
          const a = (window.snaptr = function () {
            a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments);
          });
          a.queue = [];
        }
        await script('https://sc-static.net/scevent.min.js');
        window.snaptr('init', c.pixel_id);
      }
      if (d.slug === 'clarity' && c.project_id) {
        window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
        await script(`https://www.clarity.ms/tag/${c.project_id}`);
      }
      if (d.slug === 'posthog' && c.project_api_key) {
        await script(`${(c.host || 'https://app.posthog.com').replace(/\/$/, '')}/static/array.js`);
        if (window.posthog?.init) {
          window.posthog.init(c.project_api_key, { api_host: c.host || 'https://app.posthog.com' });
        }
      }
      loaded.add(`init:${d.slug}`);
    } catch { /* a broken pixel must never break the storefront */ }
  }
  // Admin-authored snippets: inert text, injected only when explicitly allowed
  (config.custom_scripts || []).forEach((cs, i) => {
    const key = `custom:${cs.id || i}`;
    if (loaded.has(key)) return;
    loaded.add(key);
    try {
      const holder = document.createElement('div');
      holder.innerHTML = cs.content;
      Array.from(holder.childNodes).forEach((node) => {
        if (node.tagName === 'SCRIPT') {
          const el = document.createElement('script');
          Array.from(node.attributes || []).forEach((a) => el.setAttribute(a.name, a.value));
          el.text = node.text;
          (cs.location === 'body' ? document.body : document.head).appendChild(el);
        } else if (node.nodeType === 1) {
          (cs.location === 'body' ? document.body : document.head).appendChild(node);
        }
      });
    } catch { /* ignore */ }
  });
}

/* ── canonical event → destination fan-out ── */

function fanOut(name, payload, eventId) {
  if (!config) return;
  const value = payload.value;
  const currency = payload.currency || 'INR';
  const items = payload.items || (payload.product_id
    ? [{ product_id: payload.product_id, name: payload.product_name, price: value, quantity: payload.quantity || 1 }]
    : []);

  // GTM always receives the canonical event — tags there consume our definitions.
  if (dest('gtm') && allowed('analytics')) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `diosa_${name}`, diosa_event_id: eventId, ...payload });
  }

  const ga = dest('ga4');
  if (ga && allowed('analytics') && ga.event_map?.[name] && window.gtag) {
    const params = { event_id: eventId, currency };
    if (value !== undefined) params.value = value;
    if (payload.order_id) params.transaction_id = payload.order_id;
    if (payload.coupon_code) params.coupon = payload.coupon_code;
    if (payload.search_term) params.search_term = payload.search_term;
    if (items.length) {
      params.items = items.map((i) => ({ item_id: i.product_id, item_name: i.name, price: i.price, quantity: i.quantity || 1 }));
    }
    window.gtag('event', ga.event_map[name], params);
  }

  const ads = dest('google-ads');
  if (ads && allowed('ads') && window.gtag) {
    const labelKey = ads.event_map?.[name];
    const label = labelKey && ads.config?.[labelKey];
    if (label) {
      window.gtag('event', 'conversion', {
        send_to: `${ads.config.conversion_id}/${label}`,
        value, currency, transaction_id: payload.order_id || eventId,
      });
    }
  }

  const meta = dest('meta-pixel');
  if (meta && allowed('ads') && meta.event_map?.[name] && window.fbq) {
    const custom = { currency };
    if (value !== undefined) custom.value = value;
    if (items.length) {
      custom.contents = items.map((i) => ({ id: i.product_id, quantity: i.quantity || 1, item_price: i.price }));
      custom.content_type = 'product';
    }
    // eventID shared with the Conversions API so Meta counts one conversion, not two
    window.fbq('track', meta.event_map[name], custom, { eventID: eventId });
  }

  const tt = dest('tiktok-pixel');
  if (tt && allowed('ads') && tt.event_map?.[name] && window.ttq?.track) {
    window.ttq.track(tt.event_map[name], {
      value, currency, event_id: eventId,
      contents: items.map((i) => ({ content_id: i.product_id, content_name: i.name, price: i.price, quantity: i.quantity || 1 })),
    });
  }

  const pin = dest('pinterest-tag');
  if (pin && allowed('ads') && pin.event_map?.[name] && window.pintrk) {
    window.pintrk('track', pin.event_map[name], { value, currency, event_id: eventId });
  }

  const snap = dest('snapchat-pixel');
  if (snap && allowed('ads') && snap.event_map?.[name] && window.snaptr) {
    window.snaptr('track', snap.event_map[name], { price: value, currency, transaction_id: payload.order_id });
  }

  if (dest('posthog') && allowed('analytics') && window.posthog?.capture) {
    window.posthog.capture(name, { ...payload, $insert_id: eventId });
  }
}

/* ── our own collection endpoint ── */

const flush = async () => {
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  try {
    await axios.post(`${API}/api/events/collect`, {
      anonymous_id: anonymousId(),
      session_id: sessionId(),
      context: {
        page_url: window.location.href,
        referrer: document.referrer || undefined,
        fbp: cookie('_fbp'), fbc: cookie('_fbc'),
        ...captureAttribution(),
      },
      events: batch,
    }, { withCredentials: true });
  } catch { /* never surface a tracking failure to a shopper */ }
};

/** Report one canonical event. Safe to call from anywhere; never throws. */
export function track(name, payload = {}) {
  try {
    const eventId = uuid();
    queue.push({ name, event_id: eventId, ts: new Date().toISOString(), payload });
    fanOut(name, payload, eventId);
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, 900);
  } catch { /* ignore */ }
}

export function trackPageView(path) {
  track('page_view', { page_url: window.location.origin + (path || window.location.pathname) });
}

/** Ties the anonymous history to the real customer after login/registration. */
export async function identify() {
  try {
    await axios.post(`${API}/api/events/identify`,
      { anonymous_id: anonymousId(), context: captureAttribution() },
      { withCredentials: true });
  } catch { /* ignore */ }
}

export async function initTracking() {
  try {
    anonymousId(); sessionId(); captureAttribution();
    const { data } = await axios.get(`${API}/api/tracking/config`);
    config = data;
    if (config.collect_enabled === false) { config.destinations = []; return config; }
    await loadDestinations();
    return config;
  } catch {
    config = { destinations: [], consent_mode: 'none' };
    return config;
  }
}

export const trackingConfig = () => config;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => { flush(); });
  // The anonymous id travels on every API call so the backend can stitch identity
  // and attribution without a second customer store.
  axios.interceptors.request.use((cfg) => {
    try {
      if ((cfg.url || '').startsWith(API)) {
        cfg.headers = cfg.headers || {};
        cfg.headers['X-Diosa-Anonymous-Id'] = anonymousId();
      }
    } catch { /* ignore */ }
    return cfg;
  });
}
